import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { ArtData } from '@/database/art'
import CountdownClock from './CountdownClock'
import '@/css/mintoverview.css'
import { Context } from '@/components/Context'
import Divider from '@/components/Divider'
import { transferFeeText } from '@/utils/formatting'
import Box from '@/components/boxes/Box'
import Loader from '@/components/Loader'
import ListingOverview from '@/components/mint/ListingOverview'
import DetailsOverview from '@/components/mint/DetailsOverview'
import LoadImageButtonOnDemand from '@/components/LoadImageButtonOnDemand'
import VotesTooltip from '@/components/tooltips/VotesTooltip'
import { UpdateType } from '@/types/websocket'

const MintOverview = (): ReactElement => {
    const { setActiveUpdates, adminConfiguration, proposalData } = useContext(Context)

    const [isModalDetailsOpen, setIsModalDetailsOpen] = useState(false)
    const [highestVotedArt, setHighestVotedArt] = useState<ArtData>({} as ArtData)
    const [modalArtData, setModalArtData] = useState<ArtData>({} as ArtData)

    const openModalMintCardDetails = (artData: ArtData) => {
        if (artData) {
            setModalArtData(artData)
            setIsModalDetailsOpen(true)
        }
    }

    const closeModal = () => {
        setIsModalDetailsOpen(false)
        window.location.hash = `#mint`
    }

    useEffect(() => {
        setActiveUpdates([UpdateType.VOTES, UpdateType.ADMIN, UpdateType.PROPOSALS])
    }, [])

    useEffect(() => {
        const checkHashAndOpenModal = () => {
            const hash = window.location.hash
            const match = hash.match(/#art-(\d+)/)
            if (match) {
                const selectedArt = proposalData.find((art) => art.art.id === Number(match[1]))
                if (selectedArt) {
                    openModalMintCardDetails(selectedArt)
                }
            }
        }

        // Check hash on mount
        checkHashAndOpenModal()

        // Add event listener for hash changes
        window.addEventListener('hashchange', checkHashAndOpenModal, false)

        // Cleanup event listener on component unmount
        return () => window.removeEventListener('hashchange', checkHashAndOpenModal, false)
    }, [proposalData])

    useEffect(() => {
        if (proposalData?.length > 0) {
            setHighestVotedArt(proposalData[0])
        }
    }, [proposalData])

    return (
        <div>
            {!isModalDetailsOpen && <h1 className="title">Vote Which CRAZY NFT Is Minted Next</h1>}
            <div className="container">
                {!isModalDetailsOpen && (
                    <div className="votes">
                        <div className="header-container bottom">
                            <Box
                                caption={'Mint Countdown'}
                                text={
                                    <CountdownClock
                                        futureDateTime={
                                            adminConfiguration?.nextMint || new Date(Date.now() + 24 * 60 * 60 * 1000)
                                        }
                                    />
                                }
                                triggerAnimation={false}
                            />
                        </div>
                        {highestVotedArt?.image ? (
                            <div className="header-row">
                                <h1 className="">Highest Voted Art</h1>
                                <h1>
                                    <span className="gradient-text">{highestVotedArt.art.title}</span>
                                </h1>

                                <div className={'header-container'}>
                                    <div className="artwork-box">
                                        <div className="image-container">
                                            <LoadImageButtonOnDemand
                                                artData={highestVotedArt}
                                                onClick={() => openModalMintCardDetails(proposalData[0])}
                                            />
                                        </div>
                                        <div className={'header-container'}>
                                            <Box caption={'Description'} text={highestVotedArt.art.prompt} />
                                        </div>
                                        <div className={'header-container'}>
                                            <div id={'votes' + highestVotedArt.art.id} className="p-copy">
                                                <Box
                                                    caption={'Votes'}
                                                    text={highestVotedArt.claimPower.toFixed(2)}
                                                    styleCaption={'gradient-text bold'}
                                                />
                                            </div>
                                            <Box
                                                caption={'Transfer Fee'}
                                                text={transferFeeText(highestVotedArt)}
                                                textValue={highestVotedArt}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className=" nav__medium"
                                            onClick={() => openModalMintCardDetails(proposalData[0])}
                                        >
                                            Vote
                                        </button>
                                    </div>
                                </div>
                                <VotesTooltip artData={highestVotedArt} />
                            </div>
                        ) : (
                            <Loader />
                        )}
                    </div>
                )}
            </div>
            <div>
                {!isModalDetailsOpen && (
                    <div className="bottom white">
                        <Divider />
                        <h1>Mint Proposal Ranking</h1>
                    </div>
                )}
                <div>
                    {!isModalDetailsOpen && <ListingOverview openModalMintCardDetails={openModalMintCardDetails} />}
                    {isModalDetailsOpen && modalArtData && (
                        <DetailsOverview artData={modalArtData} onClose={closeModal} />
                    )}
                </div>
            </div>
        </div>
    )
}

export default MintOverview
